import type {
  AudioTranscodeInfo,
  EncodeStatusEnum,
  ImageTranscodeInfo,
  ModelTranscodeInfo,
  ThumbTranscodeInfo,
  VideoTranscodeInfo,
} from '../../../../generated';
import { isMediaUrlDifferentOrExpiring } from '../isMediaUrlDifferentOrExpiring';

/**
 * Ensures valid, un-expired stream and download URLs are preferred over newly signed URLs.
 * Each transcode key is taken into consideration to ensure pending
 * transcodes are accounted for.
 *
 * note: there may be a more robust BE solve to avoid this checking
 */
type SupportedTranscodes =
  | AudioTranscodeInfo
  | ImageTranscodeInfo
  | ThumbTranscodeInfo
  | ModelTranscodeInfo
  | VideoTranscodeInfo;

const reusableKeys = ['streamUrl', 'downloadUrl'];

export const transcodeUrlMerge = <
  T extends SupportedTranscodes,
  U extends SupportedTranscodes,
>(
  existing: readonly T[] | null | undefined,
  incoming: readonly U[] | null | undefined
): readonly T[] | readonly U[] | null | undefined => {
  const emptyExisting = !existing || existing.length === 0;
  const emptyIncoming = !incoming || incoming.length === 0;

  // protect against edge cases, default to incoming value
  if (emptyExisting || emptyIncoming) return incoming;
  if (existing.length !== incoming.length) return incoming;

  // iterate over each new incoming transcode object
  const existingIsValid = incoming.reduce(
    (existingIsValidSoFar, incomingTranscode, index) => {
      // order from query results is assured to be equal, so its safe to use index
      const existingTranscode = existing[index];

      const streamParam =
        incomingTranscode?.streamUrl?.includes('Expires') ||
        existingTranscode?.streamUrl?.includes('Expires')
          ? 'Expires'
          : 'token';

      const downloadParam =
        incomingTranscode?.downloadUrl?.includes('Expires') ||
        existingTranscode?.downloadUrl?.includes('Expires')
          ? 'Expires'
          : 'token';

      // Use incoming if no existing
      if (!existingTranscode) {
        return false;
      }

      const existingStreamUrlIsValid =
        !incomingTranscode?.streamUrl ||
        (!!existingTranscode?.streamUrl &&
          !isMediaUrlDifferentOrExpiring({
            current: existingTranscode.streamUrl,
            next: incomingTranscode?.streamUrl,
            param: streamParam,
          }));

      if (!existingStreamUrlIsValid) {
        return false;
      }

      const existingDownloadUrlIsValid =
        !incomingTranscode?.downloadUrl ||
        (!!existingTranscode?.downloadUrl &&
          !isMediaUrlDifferentOrExpiring({
            current: existingTranscode.downloadUrl,
            next: incomingTranscode?.downloadUrl,
            param: downloadParam,
          }));

      if (!existingDownloadUrlIsValid) {
        return false;
      }

      /**
       * if the asset is the same, let's double check the encodeStatus to see
       * if it's viewable.
       */
      const isViewable = (
        ['SUCCESS', 'WARNING'] as Array<EncodeStatusEnum>
      ).includes(incomingTranscode?.encodeStatus);
      /**
       * if not viewable, always use incoming
       */
      if (!isViewable) {
        return false;
      }
      /**
       * if other properties are different (e.g. encodeStatus), use incoming
       **/
      const areOtherPropertiesEqual = Object.entries(incomingTranscode).every(
        ([key, value]) => {
          if (reusableKeys.includes(key)) return true;
          if (existingTranscode[key] !== value) {
            return false;
          }
          return true;
        }
      );
      if (!areOtherPropertiesEqual) {
        return false;
      }

      return existingIsValidSoFar && true;
    },
    true
  );

  if (existingIsValid) return existing;
  return incoming;
};
